import { Assert } from '@cotera/utilities';
import { BaseModel } from '../actions/framework';
import { DG } from '@cotera/sdk/core';
import { TargetableModel } from '../actions/framework/types';
import { ReadonlyWatchable, Watchable } from '../actions/framework/watchable';
import { err, ok, Result } from 'neverthrow';
import { v4 } from 'uuid';
import { Routes } from '../routes';

export class ToolModel extends BaseModel implements TargetableModel {
  private _isTargeted: boolean = false;

  readonly formState: Watchable<{
    name: string;
  }>;

  // Store tool metadata (toolId from database)
  readonly toolMetadata: Watchable<{
    toolId: string;
    toolConnectionId: string;
  } | null>;

  readonly errors: ReadonlyWatchable<DG.Graph.GraphError[]>;
  readonly hasBeenTargeted: Watchable<boolean>;
  readonly changes: ReadonlyWatchable<Record<string, boolean>>;
  readonly internalCompiler: ReadonlyWatchable<DG.GraphCompiler>;
  readonly graph: ReadonlyWatchable<DG.BuildExplorer>;
  readonly newOperations = Watchable.fromValue<DG.OperationData[]>([]);
  readonly pageTitle: ReadonlyWatchable<string>;
  readonly name: ReadonlyWatchable<string>;
  readonly route: ReadonlyWatchable<string>;
  /**
   * The saved name in the base graph - updated when operations are staged
   * This is the source of truth for the tool's name in the graph
   */
  readonly currentNameInGraph: Watchable<string | null>;
  /**
   * the identity in the graph - null if not exists yet
   */
  readonly toolIdentity: ReadonlyWatchable<DG.Graph.Identity | null>;

  constructor(
    private readonly inputs: {
      name: string;
      graph: ReadonlyWatchable<DG.BuildExplorer>;
      compiler: ReadonlyWatchable<DG.GraphCompiler>;
    },
    readonly opts: {
      id?: string;
      targeted?: boolean;
      isNew?: boolean;
    } = {}
  ) {
    super(opts.id ?? inputs.name);

    this._isTargeted = opts.targeted ?? false;

    this.formState = Watchable.fromValue({
      name: inputs.name,
    });

    this.name = Watchable.from((get) => {
      return get(this.formState).name;
    });

    this.pageTitle = Watchable.from((get) => {
      return get(this.name);
    });

    this.route = Watchable.from((get) => {
      return Routes.tool.forId(get(this.name)).toString();
    });

    this.internalCompiler = Watchable.from((get) => {
      const baseCompiler = get(inputs.compiler);

      return baseCompiler.commit({
        actorId: v4(),
        message: null,
        operations: [...get(this.newOperations)],
        timestamp: new Date(),
      });
    });

    this.graph = Watchable.from((get) => {
      return get(this.internalCompiler)
        .compile()
        .match(
          (x) => x,
          (e) => e.lastGoodBuild
        );
    });

    const initialToolMetadata = () => {
      const toolNode = inputs.graph.snapshot().nodeMap[inputs.name];
      if (toolNode === undefined) {
        return null;
      }
      if (toolNode.t !== 'tool' || toolNode.def.t !== 'ffi') {
        return null;
      }

      return {
        toolId: toolNode.def.toolId,
        toolConnectionId: toolNode.def.toolConnectionId,
      };
    };

    this.toolMetadata = Watchable.fromValue(initialToolMetadata());

    // Initialize currentNameInGraph with the name from the base graph
    // This will be updated when operations are staged via markAsStaged()
    this.currentNameInGraph = Watchable.fromValue<string | null>(
      opts.isNew ? null : inputs.name
    );

    this.toolIdentity = Watchable.from((get) => {
      const savedName = get(this.currentNameInGraph);
      if (savedName === null) {
        return null;
      }

      // Look up in the BASE graph (without new operations) using the saved name
      const baseGraph = get(inputs.graph);
      const toolNode = baseGraph.nodeMap[savedName];

      if (toolNode === undefined) {
        return null;
      }
      if (toolNode.t !== 'tool') {
        return null;
      }
      return toolNode.meta.identity;
    });

    this.errors = Watchable.from((get) => {
      const sym = get(this.graph).forSymbol(get(this.name));

      if (sym === null) {
        return [];
      }

      return sym.builtNode.match(
        () => [],
        (e) => [e.err]
      );
    });

    this.hasBeenTargeted = Watchable.fromValue(false);

    this.changes = Watchable.from((get) => {
      const currentName = get(this.currentNameInGraph);
      const formName = get(this.name);
      return {
        name: opts.isNew
          ? true
          : currentName !== null && currentName !== formName,
      };
    });
  }

  get isTargeted() {
    return this._isTargeted;
  }

  markAsStaged(): void {
    // Update the saved name to the current name now that changes are staged
    this.currentNameInGraph.set(this.name.snapshot());
    // Clear new operations since they've been staged
    this.newOperations.set([]);
  }

  target(): void {
    this._isTargeted = true;
    this.hasBeenTargeted.set(true);
  }

  markAsNotTargeted(): void {
    this._isTargeted = false;
  }

  compile(
    props: {
      setOperations?: boolean;
    } = { setOperations: true }
  ): Result<
    { t: 'tool' },
    | { t: 'build'; e: DG.CommitsBuildFailure | { t: string; message: string } }
    | { t: 'tc'; e: DG.Graph.GraphError }
  > {
    const internalCompiler = this.inputs.compiler.snapshot();
    const compiler = internalCompiler;

    const nextOps = this.makeOpsFromState();

    if (nextOps.isErr()) {
      return err({ t: 'build', e: nextOps.error });
    }

    const compilationResult = compiler
      .commit({
        actorId: v4(),
        message: null,
        operations: [...nextOps.value],
        timestamp: new Date(),
      })
      .compile();

    if (compilationResult.isErr()) {
      return err({ t: 'build', e: compilationResult.error.err });
    }

    const symbol = compilationResult.value.forSymbol(this.name.snapshot());

    Assert.assert(symbol !== null);

    if (symbol.builtNode.isErr()) {
      return err({ t: 'tc', e: symbol.builtNode.error.err });
    }

    if (props.setOperations) {
      this.newOperations.set(nextOps.value);
    }

    return ok({
      t: 'tool',
    });
  }

  private makeOpsFromState(): Result<
    DG.OperationData[],
    { t: string; message: string }
  > {
    const name = this.name.snapshot();

    if (this.toolIdentity.snapshot() === null) {
      // Create the tool node using FFI definition with toolId from metadata
      const metadata = this.toolMetadata.snapshot();
      if (metadata === null) {
        return err({
          t: 'missing-tool-metadata',
          message: 'Tool metadata (toolId) is required to create a tool',
        });
      }

      return ok([
        DG.Operations.createTool({
          symbol: name,
          def: {
            t: 'ffi',
            type: name.replaceAll('/', '_'),
            toolId: metadata.toolId,
            toolConnectionId: metadata.toolConnectionId,
          },
        }),
      ]);
    }

    const ops: DG.OperationData[] = [...this.newOperations.snapshot()];

    const nameHasChanged = name !== this.currentNameInGraph.snapshot();

    const toolSymbol = this.graph
      .snapshot()
      .forSymbol(this.currentNameInGraph.snapshot()!);

    if (toolSymbol === null) {
      return err({
        t: 'tool-not-found',
        message: `Tool not found: ${this.currentNameInGraph.snapshot()!}`,
      });
    }

    if (nameHasChanged && !this.isNew) {
      const renameOps = toolSymbol.makeRenameOps({
        to: name,
      });

      if (renameOps.isErr()) {
        return err({
          t: 'rename-failure',
          message: JSON.stringify(renameOps.error),
        });
      }
      ops.push(...renameOps.value);
    }

    // For now, we don't have update operations for tools
    // This will be implemented when the tool editor UI is built

    return ok(ops);
  }

  get isNew() {
    return this.currentNameInGraph.snapshot() === null;
  }
}
